// 全局购物车请求管理器 - 防止 429 错误
// 这个文件必须在所有其他脚本之前加载

(function() {
  'use strict';

  // 配置
  const CONFIG = {
    MIN_INTERVAL: 1000,        // 最小请求间隔：1秒
    MAX_QUEUE_SIZE: 10,        // 最大队列长度
    REQUEST_TIMEOUT: 30000,    // 请求超时：30秒
    RETRY_DELAY: 2000,         // 429 错误重试延迟：2秒
    MAX_RETRIES: 2             // 最大重试次数
  };

  // 请求队列
  const queue = [];
  let processing = false;
  let lastRequestTime = 0;
  let requestCount = 0;
  let requestTimes = [];

  // 统计信息
  const stats = {
    total: 0,
    success: 0,
    failed: 0,
    throttled: 0,
    retried: 0
  };

  // 日志函数（生产环境禁用）
  function log(level, message, data) {
    // 仅在开发环境输出日志
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      const prefix = '[CartRequestManager]';
      if (level === 'error') {
        console.error(prefix, message, data || '');
      } else if (level === 'warn') {
        console.warn(prefix, message, data || '');
      } else {
        console.log(prefix, message, data || '');
      }
    }
  }

  // 清理过期的请求时间记录
  function cleanupRequestTimes() {
    const oneMinuteAgo = Date.now() - 60000;
    requestTimes = requestTimes.filter(t => t > oneMinuteAgo);
  }

  // 检查请求频率
  function checkRequestRate() {
    cleanupRequestTimes();

    // 如果最近1分钟内请求超过30次，发出警告
    if (requestTimes.length > 30) {
      log('warn', `High request rate: ${requestTimes.length} requests in last minute`);
      return false;
    }

    return true;
  }

  // 添加请求到队列
  function enqueue(requestFn, options = {}) {
    return new Promise((resolve, reject) => {
      // 检查队列长度
      if (queue.length >= CONFIG.MAX_QUEUE_SIZE) {
        stats.throttled++;
        log('warn', 'Queue is full, rejecting request');
        reject(new Error('Cart request queue is full. Please wait a moment.'));
        return;
      }

      // 检查请求频率
      if (!checkRequestRate()) {
        stats.throttled++;
        log('warn', 'Request rate too high, rejecting request');
        reject(new Error('Too many cart requests. Please slow down.'));
        return;
      }

      // 添加到队列
      queue.push({
        requestFn,
        resolve,
        reject,
        retryCount: 0,
        options: options || {}
      });

      stats.total++;
      log('info', `Request queued. Queue length: ${queue.length}`);

      // 开始处理队列
      processQueue();
    });
  }

  // 处理队列
  async function processQueue() {
    if (processing || queue.length === 0) {
      return;
    }

    processing = true;

    while (queue.length > 0) {
      const item = queue.shift();
      const { requestFn, resolve, reject, retryCount, options } = item;

      try {
        // 确保请求间隔
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTime;
        if (timeSinceLastRequest < CONFIG.MIN_INTERVAL) {
          const delay = CONFIG.MIN_INTERVAL - timeSinceLastRequest;
          log('info', `Waiting ${delay}ms before next request`);
          await sleep(delay);
        }

        // 记录请求时间
        lastRequestTime = Date.now();
        requestTimes.push(lastRequestTime);
        requestCount++;

        log('info', `Processing request #${requestCount}`);

        // 执行请求（带超时）
        const result = await Promise.race([
          requestFn(),
          timeout(CONFIG.REQUEST_TIMEOUT)
        ]);

        stats.success++;
        log('info', 'Request succeeded');
        resolve(result);

      } catch (error) {
        log('error', 'Request failed', error.message);

        // 处理 429 错误
        if (error.status === 429 && retryCount < CONFIG.MAX_RETRIES) {
          stats.retried++;
          log('warn', `429 error, retrying (attempt ${retryCount + 1}/${CONFIG.MAX_RETRIES})`);

          // 重新加入队列
          queue.unshift({
            requestFn,
            resolve,
            reject,
            retryCount: retryCount + 1,
            options
          });

          // 等待后重试
          await sleep(CONFIG.RETRY_DELAY * (retryCount + 1));
        } else {
          stats.failed++;
          reject(error);
        }
      }

      // 请求之间的额外延迟
      if (queue.length > 0) {
        await sleep(500);
      }
    }

    processing = false;
    log('info', 'Queue processing completed');
  }

  // 辅助函数：延迟
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 辅助函数：超时
  function timeout(ms) {
    return new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), ms)
    );
  }

  // 获取统计信息
  function getStats() {
    return {
      ...stats,
      queueLength: queue.length,
      processing: processing,
      requestsLastMinute: requestTimes.length,
      lastRequestTime: lastRequestTime
    };
  }

  // 清空队列
  function clearQueue() {
    const count = queue.length;
    queue.length = 0;
    log('warn', `Cleared ${count} pending requests`);
  }

  // 包装 fetch 请求
  function wrapFetch(url, options = {}) {
    return enqueue(() => {
      return fetch(url, options).then(response => {
        if (!response.ok) {
          const error = new Error(`HTTP ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return response.json();
      });
    });
  }

  // 包装 jQuery AJAX 请求
  function wrapAjax(settings) {
    // 检查 jQuery 是否可用
    if (typeof $ === 'undefined' || typeof $.ajax === 'undefined') {
      log('error', 'jQuery is not available, falling back to fetch');
      // 回退到 fetch
      return wrapFetch(settings.url, {
        method: settings.type || settings.method || 'GET',
        headers: settings.headers || {},
        body: settings.data ? (typeof settings.data === 'string' ? settings.data : JSON.stringify(settings.data)) : undefined
      });
    }

    return enqueue(() => {
      return new Promise((resolve, reject) => {
        $.ajax({
          ...settings,
          success: resolve,
          error: (xhr, status, error) => {
            const err = new Error(error || `HTTP ${xhr.status}`);
            err.status = xhr.status;
            reject(err);
          }
        });
      });
    });
  }

  // 导出到全局
  window.CartRequestManager = {
    enqueue,
    wrapFetch,
    wrapAjax,
    getStats,
    clearQueue,

    // 便捷方法
    addToCart: function(variantId, quantity = 1) {
      return wrapFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `id=${variantId}&quantity=${quantity}`
      });
    },

    updateCart: function(updates) {
      return wrapFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
    },

    getCart: function() {
      return wrapFetch('/cart.js');
    },

    clearCart: function() {
      return wrapFetch('/cart/clear.js', { method: 'POST' });
    }
  };

  // 定期清理请求时间记录
  setInterval(cleanupRequestTimes, 60000);

  // 定期输出统计信息（仅在开发模式）
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
    setInterval(() => {
      const stats = getStats();
      if (stats.total > 0) {
        log('info', 'Stats:', stats);
      }
    }, 30000);
  }

  log('info', 'Cart Request Manager initialized');

})();
